import type p5 from 'p5';
import type { PoseData, VisualConfig, PoseLandmark, Color } from '../../../types';
import type { MotionData } from '../../../utils/MotionTracker';
import type { IArtisticRenderer } from './IArtisticRenderer';
import { interpolateColor } from '../utils/ColorUtils';

/**
 * A single drifting particle.
 */
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Age in seconds */
  age: number;
  /** Lifespan in seconds */
  life: number;
  size: number;
}

/** Emitter point keyed to a landmark and the limb motion that drives it. */
interface Emitter {
  landmark: keyof PoseData['landmarks'];
  motionKey: keyof MotionData;
}

/**
 * Particle Trails Visualization.
 *
 * Emits particles from the body's extremities (wrists, ankles, head). Emission
 * rate and initial speed scale with each limb's motion intensity, so fast
 * movement throws out bright, fast streams while stillness produces a gentle
 * shimmer. Particles drift, decelerate, and fade over their lifespan.
 *
 * State is per-person: each tracked person owns one renderer instance.
 */
export class ParticleTrailsRenderer implements IArtisticRenderer {
  private particles: Particle[] = [];
  private lastUpdateTime = 0;

  private readonly maxParticles = 300;
  private readonly baseLife = 1.2; // seconds

  /** Fractional emitter carry-over so low rates still emit smoothly. */
  private emitAccumulator = 0;

  private readonly emitters: Emitter[] = [
    { landmark: 'leftWrist', motionKey: 'leftForearm' },
    { landmark: 'rightWrist', motionKey: 'rightForearm' },
    { landmark: 'leftAnkle', motionKey: 'leftShin' },
    { landmark: 'rightAnkle', motionKey: 'rightShin' },
    { landmark: 'nose', motionKey: 'head' },
  ];

  render(p: p5, pose: PoseData, config: VisualConfig, motionData: MotionData): void {
    if (!pose.detected) {
      return;
    }

    const currentTime = performance.now() / 1000;
    if (this.lastUpdateTime === 0) {
      this.lastUpdateTime = currentTime;
    }
    let deltaTime = currentTime - this.lastUpdateTime;
    this.lastUpdateTime = currentTime;
    // Clamp delta to avoid huge bursts after a tab is backgrounded
    if (deltaTime <= 0 || deltaTime > 0.1) {
      deltaTime = 1 / 30;
    }

    this.emitParticles(pose, motionData, deltaTime, p);
    this.updateParticles(deltaTime);
    this.renderParticles(p, config);
  }

  reset(): void {
    this.particles = [];
    this.lastUpdateTime = 0;
    this.emitAccumulator = 0;
  }

  /**
   * Emit new particles from each visible emitter based on its motion intensity.
   */
  private emitParticles(pose: PoseData, motionData: MotionData, deltaTime: number, p: p5): void {
    for (const emitter of this.emitters) {
      const landmark = pose.landmarks[emitter.landmark] as PoseLandmark;
      if (!landmark || landmark.visibility < 0.5) {
        continue;
      }

      const motion = motionData[emitter.motionKey];
      const intensity = motion?.intensity ?? 0;

      // Base trickle plus intensity-driven burst (particles per second).
      const rate = 4 + intensity * 60;
      this.emitAccumulator += rate * deltaTime;

      let toEmit = Math.floor(this.emitAccumulator);
      if (toEmit <= 0) {
        continue;
      }
      this.emitAccumulator -= toEmit;
      toEmit = Math.min(toEmit, 12); // cap per-emitter per frame

      const px = landmark.x * p.width;
      const py = landmark.y * p.height;
      const dir = motion?.direction ?? { x: 0, y: 0 };
      const speed = (30 + intensity * 220);

      for (let i = 0; i < toEmit; i++) {
        if (this.particles.length >= this.maxParticles) {
          break;
        }
        // Spread around the motion direction with random jitter
        const angleJitter = (Math.random() - 0.5) * Math.PI;
        const baseAngle = Math.atan2(dir.y, dir.x);
        const angle = (dir.x === 0 && dir.y === 0)
          ? Math.random() * Math.PI * 2
          : baseAngle + angleJitter;
        const spd = speed * (0.4 + Math.random() * 0.6);

        this.particles.push({
          x: px,
          y: py,
          vx: Math.cos(angle) * spd,
          vy: Math.sin(angle) * spd,
          age: 0,
          life: this.baseLife * (0.6 + Math.random() * 0.8),
          size: 3 + Math.random() * 4 + intensity * 4,
        });
      }
    }
  }

  /**
   * Advance particle positions and ages, removing expired ones.
   */
  private updateParticles(deltaTime: number): void {
    const drag = Math.pow(0.4, deltaTime); // velocity decays over time
    const next: Particle[] = [];
    for (const particle of this.particles) {
      particle.age += deltaTime;
      if (particle.age >= particle.life) {
        continue;
      }
      particle.vx *= drag;
      particle.vy *= drag;
      // Slight upward drift for a floating feel
      particle.vy -= 8 * deltaTime;
      particle.x += particle.vx * deltaTime;
      particle.y += particle.vy * deltaTime;
      next.push(particle);
    }
    this.particles = next;
  }

  /**
   * Draw all particles, fading from primary to secondary color over their life.
   */
  private renderParticles(p: p5, config: VisualConfig): void {
    p.noStroke();
    const primary: Color = config.primaryColor;
    const secondary: Color = config.secondaryColor;

    for (const particle of this.particles) {
      const lifeFraction = particle.age / particle.life;
      const color = interpolateColor(primary, secondary, lifeFraction);
      const alpha = (1 - lifeFraction) * 255 * config.primaryColor.a;
      p.fill(color.r, color.g, color.b, alpha);
      const size = particle.size * (1 - lifeFraction * 0.5);
      p.circle(particle.x, particle.y, size);
    }
  }
}
